module Widgets.Form.Expectations where

import Common

import API.Expectations (Expectation, OccupationType, ExpectationFilterEntry, courseExpectationsQuery, courseListSelAff, employerPosSearchSelAff, nocExpectationsQuery, occupationListSelAff, positionExpectationsQuery, tradeExpectationsQuery)
import API.Expectations as Expectations
import API.Types (StudentID)
import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Argonaut (decodeJson, encodeJson)
import Data.Array (catMaybes)
import Data.Array as Arr
import Data.List (List)
import Data.Maybe (maybe)
import Error (ErrorContext(..), reportError, wrapJsonError')
import GraphQL.Join (runGraphQL)
import GraphQL.Type (dummyId, innerId, maybeId, typedId)
import Widgets.BetterSelect (Select, SearchAff, defSelectConfig, initSelect', mapSearchAff, setSelected)
import Widgets.ExpectationPicker (ExpectationPickerTabBase, expectationPicker, initExpectationPicker, rankFilterEntry, selectedExpectations)
import Widgets.Form.Class (class FormWidget, FormContext, fieldAccessProps, fieldCanView, fieldId, formWidgetState, setData)
import Widgets.Form.Dynamic (DynFormWidget, wrapFormWidget)
import Widgets.Form.List (modalListWidget)
import Widgets.Form.Simple (FormError(..))
import Widgets.Form.SubForm (SubForm)

type ExpectationsOpts r =
    { id :: String
    , label :: String
    , cls :: String
    | r }


courseExpectationsWidget :: SubForm -> ExpectationsOpts () -> DynFormWidget
courseExpectationsWidget subform { id, label, cls } = wrapFormWidget $ modalListWidget
    { id, label, cls
    , addBtns:
        [ { label: lookupTranslation "course-choose-expectation"
          , newEntries: \fc -> do
                exps <- Arr.fromFoldable <$> runCourseExpectationChooser fc
                traverse (widgetFromExpectation fc subform) exps
          }
        , { label: lookupTranslation "course-insert-expectation"
          , newEntries: \_ -> pure [wrapFormWidget subform] }
        ]
    , load
    , loadExist: \fc _ json -> load fc json
    }
  where load fc json = wrapFormWidget <$> widgetFromExpectationData fc subform json

runCourseExpectationChooser :: FormContext -> Widget HTML (List Expectation)
runCourseExpectationChooser fc = do
    -- TODO: Show loading indicator during this.
    let state = initExpectationPicker { tabs }
    endState <- expectationPicker state
    pure $ selectedExpectations endState
  where
    select = initSelect' "course" (courseListExpSelAff (maybe dummyId _.student.id fc.doc)) $ defSelectConfig
                { placeholder = Just $ lookupTranslation "select-choose-search" }
    tabs :: Array (ExpectationPickerTabBase () ())
    tabs =
        [ { label: lookupTranslation "expectations-all-curriculum"
          , query: runGraphQL <<< courseExpectationsQuery [] <<< typedId
          , select
          }
        , { label: lookupTranslation "expectations-overall"
          , query: runGraphQL <<< courseExpectationsQuery [Expectations.Overall] <<< typedId
          , select
          }
        -- , { label: lookupTranslation "expectations-course-favs"
        --   , query, select
        --   }
        ]

courseListExpSelAff :: StudentID -> SearchAff ExpectationFilterEntry
courseListExpSelAff student = mapSearchAff toFE rankFilterEntry (courseListSelAff student)
  where
    toFE { id } = { id: innerId id }

occuListExpSelAff :: OccupationType -> SearchAff ExpectationFilterEntry
occuListExpSelAff occ = mapSearchAff toFE rankFilterEntry $ occupationListSelAff occ
  where
    toFE { id } = { id }


employerExpectationsWidget :: SubForm -> ExpectationsOpts () -> DynFormWidget
employerExpectationsWidget subform { id, label, cls } = wrapFormWidget $ modalListWidget
    { id, label, cls
    , addBtns:
        [ { label: lookupTranslation "employer-choose-expectation"
          , newEntries: \fc -> do
                exps <- Arr.fromFoldable <$> runEmployerExpectationChooser fc
                traverse (widgetFromExpectation fc subform) exps
          }
        , { label: lookupTranslation "employer-insert-expectation"
          , newEntries: \_ -> pure [wrapFormWidget subform] }
        ]
    , load
    , loadExist: \fc _ json -> load fc json
    }
  where load fc json = wrapFormWidget <$> widgetFromExpectationData fc subform json

runEmployerExpectationChooser :: FormContext -> Widget HTML (List Expectation)
runEmployerExpectationChooser fc = do
    -- TODO: Show loading indicator during this.
    let state = initExpectationPicker { tabs }
    endState <- expectationPicker state
    pure $ selectedExpectations endState
  where
    preselectEmployer :: Select ExpectationFilterEntry -> Select ExpectationFilterEntry
    preselectEmployer state = setSelected state $ do
        emp <- fc.doc >>= _.employer
        pos <- fc.doc >>= _.position
        pure { label: emp.company <> " - " <> pos.label, value: { id: innerId pos.id } }

    tabs :: Array (ExpectationPickerTabBase () ())
    tabs =
        [ { label: lookupTranslation "expectations-noc"
          , query: runGraphQL <<< nocExpectationsQuery
          , select: initSelect' "noc" (occuListExpSelAff "NOC") $
              defSelectConfig { placeholder = Just $ lookupTranslation "select-choose-search" }
          }
        , { label: lookupTranslation "expectations-employer-tab"
          , query: runGraphQL <<< positionExpectationsQuery
          , select: preselectEmployer $
                initSelect' "employer-pos" (employerPosSearchSelAff $ maybeId $ map _.id fc.doc) $
                defSelectConfig { placeholder = Just $ lookupTranslation "select-choose-search" }
          }
        , { label: lookupTranslation "expectations-trade"
          , query: runGraphQL <<< tradeExpectationsQuery
          , select: initSelect' "trade" (occuListExpSelAff "TRADE") $
              defSelectConfig { placeholder = Just $ lookupTranslation "select-choose-search" }
          }
        ]


-- | Creates an expectation widget from an expectation chosen in the UI.
widgetFromExpectation :: FormContext -> SubForm -> Expectation -> Widget HTML DynFormWidget
widgetFromExpectation fc subform { description } =
    case widgetFromExpectationData fc subform $ encodeJson
            { expectation: description
            , learning: "" }
        of
        Right expw -> pure $ wrapFormWidget expw
        Left err -> do
            let err' = WithContext (DuringAction "while loading chosen expectation into widget") err
            _ <- liftEffect $ reportError err'
            pure $ wrapFormWidget $ FormError
                { id: "expectation-error"
                , err: StringError $
                    "Failed to load pre-defined expectation data into the expectation entry. " <>
                    "This is likely caused by the subform in the form design not matching the expected layout."
                }

widgetFromExpectationData :: FormContext -> SubForm -> Json -> Either ErrorWrapper SubForm
widgetFromExpectationData fc subform json = setData fc subform json


-- | The actual expectation widget that gets added into the form when the user
-- | adds expectations. This is combined with a list widget to make the
-- | expectations chooser.
newtype ExpectationWidget = ExpectationWidget ExpectationWidgetType
derive newtype instance showEW :: Show ExpectationWidget

type ExpectationWidgetType =
    { expectation :: String
    , learning :: String
    } -- TODO: Marks?
type ExpectationData = { id :: String, expectation :: String, learning :: String }

instance expectFW :: FormWidget ExpectationWidget where
    -- These are only shown inside a list widget which ignores the ID anyway.
    getId _ = "expectation"
    isLeaf _ = false
    asField _ = Nothing
    fieldPaths fc _ = [ fieldId fc "expectation", fieldId fc "learning" ]
    toWidget fc (ExpectationWidget state) = formWidgetState <$> ExpectationWidget <$> D.details
        [ P.className "expectation"
        -- This is made unique by the list widget containing this adding the
        -- index to the parent IDs
        , P.key $ fieldId fc "expectation-entry"
        , P._id $ fieldId fc "expectation-entry"
        , P.open true ]
        [ D.summary' [ D.text state.expectation ], body ]
      where
        body = D.div [P.className "expectation-body"] $ catMaybes
            [ expWidget, learningWidget ]

        labelledTextArea :: String -> String -> String -> Maybe (Widget HTML String)
        labelledTextArea id value label
            | fieldCanView fc id = Just $ D.div'
                [ D.label [P.htmlFor $ fieldId fc id] [D.text label]
                , D.textarea
                    ( [ P.className "widget-input"
                      , P.value value, P._id $ fieldId fc id
                      , P.unsafeTargetValue <$> P.onInput
                      ] <> fieldAccessProps fc id ) []
                ]
            | otherwise = Nothing

        learningWidget = map (\widget -> state { learning = _ } <$> widget) $
            labelledTextArea "learning" state.learning (lookupTranslation "learning-demonstration")

        expWidget = map (\widget -> state { expectation = _ } <$> widget) $
            labelledTextArea "expectation" state.expectation (lookupTranslation "expectation")

    getData _ (ExpectationWidget { expectation, learning }) =
        encodeJson { expectation, learning }

    setData _ _ json = do
        edata :: ExpectationData <- wrapJsonError' json $ decodeJson json
        -- FIXME: We can't really determine if this was a custom expectation.
        -- This may change when we know what format the data will actually be.
        Right $ ExpectationWidget
            { expectation: edata.expectation
            , learning: edata.learning
            }
